// src/hooks/useTicketStages.js
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../service/API';

/**
 * Load list of service types (["FCL Export", "LCL Import", ...])
 */
export function useServiceTypes() {
  const apiClient = useApiClient();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Keep a manual refetch for consumers, but DON'T depend on it in effects.
  const refetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await apiClient.get('/stages/services');
      setServices(Array.isArray(data) ? data.filter(Boolean) : []);
    } catch (e) {
      setServices([]);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await apiClient.get('/stages/services');
        if (!cancelled) setServices(Array.isArray(data) ? data.filter(Boolean) : []);
      } catch (e) {
        if (!cancelled) {
          setServices([]);
          setError(e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // ⛔️ DO NOT include `refetch` or `apiClient` here to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { services, loading, error, refetch };
}

/**
 * Load default stages for a given service
 * returns array of objects: [{ name, code, position, variantOptions? }, ...]
 */
export function useDefaultStages(serviceType) {
  const apiClient = useApiClient();
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(!!serviceType);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!serviceType) {
      setStages([]);
      setLoading(false);
      setError(null);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const { data } = await apiClient.get('/stages/default', { params: { serviceType } });
      setStages(Array.isArray(data) ? data.filter(Boolean) : []);
    } catch (e) {
      setStages([]);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [apiClient, serviceType]);

  useEffect(() => {
    let cancelled = false;

    if (!serviceType) {
      setStages([]);
      setLoading(false);
      setError(null);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await apiClient.get('/stages/default', { params: { serviceType } });
        if (!cancelled) setStages(Array.isArray(data) ? data.filter(Boolean) : []);
      } catch (e) {
        if (!cancelled) {
          setStages([]);
          setError(e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Only react to real driver: serviceType.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceType]);

  return { stages, loading, error, refetch };
}

/**
 * Load full stage catalog (for “All services” filter dropdowns, etc.)
 * returns array of objects: [{ id, code, name, isTerminal, variantOptions? }, ...]
 */
export function useStageCatalog() {
  const apiClient = useApiClient();
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const refetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await apiClient.get('/stages/catalog');
      setCatalog(Array.isArray(data) ? data.filter(Boolean) : []);
    } catch (e) {
      setCatalog([]);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await apiClient.get('/stages/catalog');
        if (!cancelled) setCatalog(Array.isArray(data) ? data.filter(Boolean) : []);
      } catch (e) {
        if (!cancelled) {
          setCatalog([]);
          setError(e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Fetch once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { catalog, loading, error, refetch };
}